import type { NavigationState, PartialState } from '@react-navigation/native';

export type MainTabRoute = 'Dashboard' | 'Goals' | 'Income' | 'Progress' | 'Report';

export const MAIN_TAB_ROUTES: MainTabRoute[] = [
  'Dashboard',
  'Goals',
  'Income',
  'Progress',
  'Report',
];

const findActiveTabInState = (
  state: NavigationState | PartialState<NavigationState> | undefined
): MainTabRoute | null => {
  if (!state?.routes?.length) return null;

  const index = state.index ?? 0;
  const route = state.routes[index];
  const name = route?.name as MainTabRoute | undefined;

  if (name && MAIN_TAB_ROUTES.includes(name)) {
    return name;
  }

  if (route?.state) {
    const nested = findActiveTabInState(route.state);
    if (nested) return nested;
  }

  for (const r of state.routes) {
    if (r.state) {
      const nested = findActiveTabInState(r.state);
      if (nested) return nested;
    }
  }

  return null;
};

/** Resolves the active bottom-tab route from any ancestor navigation state (e.g. root stack → Main → tabs). */
export const getActiveMainTab = (
  state: NavigationState | PartialState<NavigationState> | undefined
): MainTabRoute => {
  return findActiveTabInState(state) ?? 'Dashboard';
};

const findDashboardStackRoute = (
  state: NavigationState | PartialState<NavigationState> | undefined
): string | null => {
  if (!state?.routes?.length) return null;

  const index = state.index ?? 0;
  const route = state.routes[index];

  if (route?.name === 'Dashboard' && route.state?.routes?.length) {
    const stackIndex = route.state.index ?? 0;
    return route.state.routes[stackIndex]?.name ?? null;
  }

  if (route?.state) {
    const nested = findDashboardStackRoute(route.state);
    if (nested) return nested;
  }

  for (const r of state.routes) {
    if (r.state) {
      const nested = findDashboardStackRoute(r.state);
      if (nested) return nested;
    }
  }

  return null;
};

/** Active screen inside the Dashboard stack (e.g. DashboardMain, Settings, FAQ). */
export const getActiveDashboardStackRoute = (
  state: NavigationState | PartialState<NavigationState> | undefined
): string | null => findDashboardStackRoute(state);

/** True when the user is on a nested Dashboard stack screen (not the main dashboard). */
export const isDashboardStackScreen = (
  state: NavigationState | PartialState<NavigationState> | undefined
): boolean => {
  const screen = findDashboardStackRoute(state);
  return screen != null && screen !== 'DashboardMain';
};
